/**
 * Zentrale Rechteprüfung (Rollen & Rechte, Etappe 1 „Fundament“) — rein: keine Importe, kein Prisma, keine Uhr.
 *
 * Liegt doppelt in Backend und Frontend (`utils/rechte.ts`), ein Test vergleicht Byte für Byte (doppelteDateien.test.ts).
 *
 * Etappe 1 ändert für niemanden etwas: Mit den festen Standardvorlagen ergibt `darf` EXAKT das Verhalten, das
 * `permissions.ts`, `auth.middleware.ts` und `dataFilters.ts` bisher von Hand ausrechneten. Die Standardwerte sind aus diesem
 * Code abgeleitet, nicht aus den Mockups (die weichen ab). Die Äquivalenztests (rechte.aequivalenz.test.ts) frieren die
 * alten Funktionen ein und vergleichen alle Kombinationen. Funktionen ohne alten Gegenpart (z. B. Verwaltung) sind aus den
 * Seitenzugängen gelesen und stehen unter „nicht durch Test gedeckt“ im Bauplan.
 *
 * Wege eines Rechts (Funktion F, Ereignis E):
 *   überall | zugewiesen (Konto ist Mitbearbeiter von E, Spalte „zugewiesen“ der Kontorolle UND Vorlage der
 *   Mitbearbeiter-Rolle erlauben F — die Spalte ist eine Obergrenze) | einzeln (E ausgewählt und Mitbearbeiter von E) |
 *   DS-Admin (Dienststelle von E) | DS-Viewer | Ersteller. Super-Admin darf immer. Ein Verbot am Konto gilt auf allen Wegen
 *   außer für den Super-Admin.
 */

export type FunktionsArt = 'e' | 's';

export interface FunktionsDef {
  id: string;
  gruppe: string;
  titel: string;
  /** 'e' = je Ereignis, 's' = System */
  art: FunktionsArt;
  /** Gesundheitsdaten/Einschränkungen (Art. 9 DSGVO): später nur Super-Admin vergibt */
  art9?: boolean;
  /** Geltungsbereich Gliederung (Etappe 2) */
  gliederung?: boolean;
}

const d = <I extends string>(gruppe: string, id: I, titel: string, art: FunktionsArt, kennz: { art9?: true; gliederung?: true } = {}) =>
  ({ id, gruppe, titel, art, ...kennz }) as const;

/** Funktionskatalog — Kennungen wie im Mockup `docs/mockups/rechte-matrix` (plus `vRollen` aus `rechte-rollen`) */
export const FUNKTIONEN = [
  d('Ereignisse', 'eAnsehen', 'Ereignisse ansehen', 'e'),
  d('Ereignisse', 'eAnlegen', 'Ereignis anlegen', 's'),
  d('Ereignisse', 'eBearbeiten', 'Grunddaten und Schichten bearbeiten', 'e'),
  d('Ereignisse', 'eVeroeff', 'Ereignis veröffentlichen', 'e'),
  d('Ereignisse', 'eArchiv', 'Archivieren und löschen', 'e'),
  d('Ereignisse', 'eMitbearb', 'Mitbearbeiter festlegen', 'e'),
  d('Plan', 'pBearbeiten', 'Plan bearbeiten', 'e'),
  d('Plan', 'pEinteilen', 'Helfende einteilen', 'e'),
  d('Plan', 'pStruktur', 'Struktur importieren', 'e'),
  d('Plan', 'pVorlageEin', 'Vorlage einfügen', 'e'),
  d('Plan', 'pVorlageSp', 'Als Vorlage speichern oder überschreiben', 'e'),
  d('Plan', 'pLeeren', 'Plan leeren', 'e'),
  d('Plan', 'pVersion', 'Versionierung', 'e'),
  d('Plan', 'pAssistent', 'Planungsassistent', 'e'),
  d('Plan', 'pVorsort', 'Vorsortieren und Priorität', 'e'),
  d('Plan', 'pNotizen', 'Interne Notizen', 'e'),
  d('Plan', 'pHistorie', 'Historie ansehen', 'e'),
  d('Anmeldung und Information', 'aFormular', 'Anmeldung und Formular bearbeiten', 'e'),
  d('Anmeldung und Information', 'aFreigeben', 'Anmeldungen freigeben oder ablehnen', 'e'),
  d('Anmeldung und Information', 'aNachfrage', 'Nachfrage an Helfende senden', 'e'),
  d('Anmeldung und Information', 'aInfoseite', 'Infoseite bearbeiten', 'e'),
  d('Anmeldung und Information', 'aEinsatz', 'Einsatzinformationen und Dienstmappe', 'e'),
  d('Anmeldung und Information', 'aShuttle', 'Shuttle und Abfahrtsplan', 'e'),
  d('Durchführung', 'dAnwesend', 'Anwesenheit erfassen', 'e'),
  d('Durchführung', 'dStatus', 'Einsatz-Status', 'e'),
  d('Durchführung', 'dAbmeld', 'Abmeldungen bearbeiten', 'e'),
  d('Durchführung', 'dChat', 'Event-Chat', 'e'),
  d('Helfende im Ereignis', 'hHand', 'Helfende von Hand hinzufügen', 'e'),
  d('Helfende im Ereignis', 'hBearbeiten', 'Helfende bearbeiten', 'e'),
  d('Helfende im Ereignis', 'hEntfernen', 'Helfende entfernen', 'e'),
  d('Helfende im Ereignis', 'hKontakt', 'Telefon und E-Mail sehen', 'e'),
  d('Helfende im Ereignis', 'hSensibel', 'Geburtsdatum, Adresse, Allergien sehen', 'e', { art9: true }),
  d('Helfende im Ereignis', 'hBank', 'Bankdaten sehen', 'e'),
  d('Helfende im Ereignis', 'hNotiz', 'Notizen zu Helfenden', 'e'),
  d('Kartei', 'kUebernehmen', 'Kartei ansehen und übernehmen', 'e', { gliederung: true }),
  d('Kartei', 'kBearbeiten', 'Karteieinträge bearbeiten', 's', { gliederung: true }),
  d('Kartei', 'kZusammen', 'Zusammenführen und Duplikate', 's'),
  d('Kartei', 'kEinschr', 'Einschränkungen „Soll nicht“', 's', { art9: true }),
  d('Kartei', 'kNachweise', 'Nachweise und Qualifikationen prüfen', 's'),
  // Telefonbuch (#683, Modul `telefonbuch`): Adressbuch der Kartei per CardDAV aufs eigene Gerät abonnieren (nur lesend); ab Werk Administration
  d('Kartei', 'kTelefonbuch', 'Telefonbuch abonnieren', 's'),
  d('Nachbereitung', 'nAbrechnung', 'Abrechnung dieses Ereignisses', 'e'),
  d('Nachbereitung', 'nExport', 'Exporte und PDF', 'e'),
  d('Nachbereitung', 'nBackup', 'Backup des Ereignisses', 'e'),
  d('Abrechnung und Auswertung', 'bMonat', 'Monatsabschluss und Belege', 's'),
  d('Abrechnung und Auswertung', 'bAuswertung', 'Auswertung und Statistik', 's'),
  d('Abrechnung und Auswertung', 'bKalkulation', 'Kalkulationen', 's'),
  d('Abrechnung und Auswertung', 'bPlanrechner', 'Planungsrechner', 's'),
  d('Verwaltung', 'vBenutzer', 'Benutzer verwalten', 's'),
  d('Verwaltung', 'vDienststellen', 'Dienststellen', 's'),
  // E10 (V3): `vQuali` ist in zwei Funktionen geteilt — Kategorien pflegen auch Ereignisverantwortliche, Qualifikationen nur die Administration
  d('Verwaltung', 'vKategorien', 'Positions-Kategorien, SanLog-Typen, Wunschpositionen', 's'),
  d('Verwaltung', 'vQualifikationen', 'Qualifikationen', 's'),
  d('Verwaltung', 'vOrg', 'Organisationen und Gliederungen', 's'),
  d('Verwaltung', 'vMeldelink', 'Org-Meldelinks', 's'),
  d('Verwaltung', 'vEmail', 'E-Mail-Vorlagen und Versand', 's'),
  d('Verwaltung', 'vBranding', 'Branding', 's'),
  d('Verwaltung', 'vModule', 'Module und Lizenz', 's'),
  d('Verwaltung', 'vAudit', 'Audit-Log', 's'),
  d('Verwaltung', 'vLes', 'LES-Zugänge und Master-Link', 's'),
  d('Verwaltung', 'vSysBackup', 'System-Backup', 's'),
  d('Verwaltung', 'vRollen', 'Rollen und Rechte bearbeiten', 's'),
] as const satisfies readonly FunktionsDef[];

export type FunktionsId = (typeof FUNKTIONEN)[number]['id'];

const ALLE_IDS: readonly FunktionsId[] = FUNKTIONEN.map(f => f.id);
// Ohne Prototyp: `DEF['constructor']` darf nie eine Funktion liefern (Eingaben mit Schlüsseln wie `toString`, `__proto__`)
const DEF: Record<string, FunktionsDef> = Object.create(null);
FUNKTIONEN.forEach(f => { DEF[f.id] = f; });

export const funktionsDef = (id: FunktionsId): FunktionsDef => DEF[id];

/**
 * Funktionen, die einen bearbeitbaren Plan bzw. ein Schreibrecht im Ereignis voraussetzen — AUSDRÜCKLICHE Liste (keine Negativregel).
 * Ein Einzel-Ereignis-Recht (Weg „einzeln“) für so eine Funktion wirkt bei einem Mitbearbeiter mit der Rolle „Betrachten“ nie; die
 * Oberfläche bietet es nicht an, der Server lehnt es ab, `darf` wertet es dort nicht.
 * Bewusst NICHT enthalten: reine Sehen-/Export-Funktionen — hSensibel, hBank, hNotiz (Betrachter-Grenze, über Weg „einzeln“ für
 * Betrachten-Mitbearbeiter vergebbar), nAbrechnung, nExport, nBackup (lesen/exportieren) sowie alles aus der Ansicht (eAnsehen,
 * pNotizen, pHistorie, pVersion, aShuttle, dAnwesend, dStatus, dChat, hKontakt). Systemfunktionen (Art „s“) kennen kein Einzel-Ereignis.
 */
export const SCHREIB_FUNKTIONEN: readonly FunktionsId[] = [
  'eBearbeiten', 'eVeroeff', 'eArchiv', 'eMitbearb',
  'pBearbeiten', 'pEinteilen', 'pStruktur', 'pVorlageEin', 'pVorlageSp', 'pLeeren', 'pAssistent', 'pVorsort',
  'aFormular', 'aFreigeben', 'aNachfrage', 'aInfoseite', 'aEinsatz',
  'dAbmeld',
  'hHand', 'hBearbeiten', 'hEntfernen',
  'kUebernehmen',
];

export const brauchtSchreibrecht = (id: FunktionsId): boolean => SCHREIB_FUNKTIONEN.includes(id);

// ---------- Standardvorlagen ----------

/** Vorlage einer Grundrolle: zwei Spalten und optional eine Grenze, die auf allen Wegen gilt (Standard-Verbot) */
export interface RollenVorlage {
  /** Spalte „überall“ */
  ueberall: readonly FunktionsId[];
  /** Spalte „in zugewiesenen Ereignissen“ (Obergrenze zusammen mit der Mitbearbeiter-Rolle) */
  zugewiesen: readonly FunktionsId[];
  /** Auf allen Wegen gesperrt, außer eine Abweichung erlaubt die Funktion ausdrücklich */
  grenze: readonly FunktionsId[];
}

/** Vorlage eines Weges (Mitbearbeiter-Rollen, DS-Admin, DS-Viewer, Ersteller) */
export interface WegVorlage {
  erlaubt: readonly FunktionsId[];
}

/** Mitarbeitende am Ereignis sehen/tun höchstens das hier, je Mitbearbeiter-Rolle */
const ANSICHT: readonly FunktionsId[] = ['eAnsehen', 'pNotizen', 'pHistorie', 'pVersion', 'aShuttle', 'dAnwesend', 'dStatus', 'dChat', 'hKontakt'];

/** Bearbeiten und Verantwortlich ohne die Übernahme aus der Kartei — Grundlage für Ersteller, DS-Admin und die Spalten der Kontorollen */
const BEARBEITEN_OHNE_KARTEI: readonly FunktionsId[] = [
  ...ANSICHT, 'pBearbeiten', 'pEinteilen', 'pVorlageEin', 'pAssistent', 'pVorsort', 'aEinsatz',
  // Etappe 7 (E7 F3): Abmeldungen bearbeiten steht beim Server schon immer an „Plan bearbeiten“ — der Standard folgt dem Server
  'dAbmeld',
  'hHand', 'hBearbeiten', 'hSensibel', 'hBank', 'hNotiz',
];

const VERANTWORTLICH_OHNE_KARTEI: readonly FunktionsId[] = [
  ...BEARBEITEN_OHNE_KARTEI, 'eBearbeiten', 'pLeeren', 'aNachfrage', 'hEntfernen', 'nAbrechnung', 'nExport', 'nBackup',
  // Etappe 7 (E7 F1): Den Reiter Anmeldung dürfen heute alle bearbeiten, die die Grunddaten bearbeiten dürfen (u. a. Mitbearbeiter „Verantwortlich“)
  'aFormular',
];

/**
 * Die Übernahme aus der Kartei ist ein Schreibvorgang: Sie steht in den Wegvorlagen „Bearbeiten“ und „Verantwortlich“ (nicht
 * „Betrachten“). Sonst bliebe der Haken „in zugewiesenen Ereignissen“ wirkungslos, denn die Spalte ist nur eine Obergrenze zusammen
 * mit der Mitbearbeiter-Rolle. Ohne Abweichung ändert das nichts: die Spalte „zugewiesen“ der Kontorollen hat die Funktion nicht
 * (Ersteller und DS-Admin bauen auf den Listen ohne Kartei auf).
 */
const BEARBEITEN: readonly FunktionsId[] = [...BEARBEITEN_OHNE_KARTEI, 'kUebernehmen'];

const VERANTWORTLICH: readonly FunktionsId[] = [...VERANTWORTLICH_OHNE_KARTEI, 'kUebernehmen'];

/** Ersteller: Bearbeiten wie „Bearbeiten“, dazu Helfende entfernen und Löschen — aber nicht die Grunddaten (wie heute) */
const ERSTELLER: readonly FunktionsId[] = [...BEARBEITEN_OHNE_KARTEI, 'hEntfernen', 'eArchiv'];

/** DS-Admin einer Dienststelle: Ereignisse dieser Dienststelle wie ein Ereignisverantwortlicher, plus Löschen */
const DS_ADMIN: readonly FunktionsId[] = [
  // Etappe 7 (E7 F2): Antworten freigeben bleibt bei der Administration — die Abfragelink-Seite verlangt der Server heute von
  // Administration und Ereignisverantwortlichen; DS-Admins bekommen sie nur über die Wegvorlage. aFormular und dAbmeld kommen über die Listen oben.
  ...VERANTWORTLICH_OHNE_KARTEI, 'eAnlegen', 'eVeroeff', 'eArchiv', 'pStruktur', 'pVorlageSp', 'pVersion', 'aInfoseite',
  'bKalkulation',
];

const EREIGNISVERANTWORTLICHER: readonly FunktionsId[] = [
  'eAnsehen', 'eAnlegen', 'eBearbeiten', 'eVeroeff',
  'pBearbeiten', 'pEinteilen', 'pStruktur', 'pVorlageEin', 'pVorlageSp', 'pLeeren', 'pVersion', 'pAssistent', 'pVorsort', 'pNotizen', 'pHistorie',
  'aFormular', 'aFreigeben', 'aNachfrage', 'aInfoseite', 'aEinsatz', 'aShuttle',
  'dAnwesend', 'dStatus', 'dAbmeld', 'dChat',
  'hHand', 'hBearbeiten', 'hEntfernen', 'hKontakt', 'hSensibel', 'hBank', 'hNotiz',
  'kUebernehmen', 'nAbrechnung', 'nExport', 'nBackup',
  'bKalkulation', 'bPlanrechner', 'vKategorien',
];

/**
 * Etappe 9 (V2): Feste Regeln — die Funktion gehört fest zur Administration (ADMIN und Super-Admin), nie zu einer Abweichung oder
 * Wegvorlage. Eine gespeicherte Abweichung ruht (sie wirkt nicht, bleibt aber sichtbar und lässt sich zurücksetzen), neue Einstellungen
 * lehnt der Server ab. Grund bei `kEinschr`: Der Orakel-Schutz der Einschränkungen „Soll nicht“ (Art. 9) hängt an rund 20 Stellen an der Rolle.
 */
export const FESTE_REGEL_ADMINISTRATION: readonly FunktionsId[] = ['kEinschr'];
export const istFesteRegel = (f: string): boolean => FESTE_REGEL_ADMINISTRATION.includes(f as FunktionsId);

/**
 * Etappe 10 (V1, V5): Diese Funktionen gehören fest zum Super-Admin und sind nie übertragbar — nicht an Konten, nicht an Rollen, nicht an
 * Wegvorlagen. Wer sie hätte, könnte sich selbst zum Super-Admin machen (Rollen bearbeiten, Datenbank zurückspielen, Modul abschalten, den
 * Mailserver umleiten) oder Spuren und Telefonnummern erreichen. Eine gespeicherte Abweichung ruht (sie wirkt nicht), neue lehnt der Server ab.
 */
export const NIE_UEBERTRAGBAR: readonly FunktionsId[] = ['vRollen', 'vModule', 'vSysBackup', 'vAudit', 'vEmail', 'vLes'];
export const nieUebertragbar = (f: string): boolean => NIE_UEBERTRAGBAR.includes(f as FunktionsId);

/** Funktionen, die keine Abweichung kennen (Konto, Rolle, Wegvorlage): feste Regeln der Administration und nie übertragbare */
const ohneAbweichung = (f: string): boolean => istFesteRegel(f) || nieUebertragbar(f);

/** Grundrollen, die überhaupt eine Verwaltungsfunktion haben können; HELFER und jede unbekannte Grundrolle nie (fail-closed) */
export const VERWALTUNG_GRUNDROLLEN: readonly string[] = ['SUPER_ADMIN', 'ADMIN', 'EREIGNISVERANTWORTLICHER', 'MITBEARBEITER', 'BETRACHTER'];

/**
 * Etappe 10: Verwaltungsfunktionen gelten nie über den Weg DS-Admin / DS-Viewer (er ist nicht auf eine Dienststelle begrenzt) und nie für
 * die Grundrolle HELFER oder eine unbekannte Grundrolle. `true` = diese Funktion ist für das Konto grundsätzlich ausgeschlossen.
 */
const keineVerwaltungsRolle = (k: RechteKontext): boolean => !VERWALTUNG_GRUNDROLLEN.includes(k.grundrolle);

const verwaltungAusgeschlossen = (k: RechteKontext, f: FunktionsId): boolean => DEF[f].gruppe === 'Verwaltung' && !VERWALTUNG_GRUNDROLLEN.includes(k.grundrolle);

/** Alles, was der Administration (ohne Super-Admin) vorbehalten bleibt, ist hier NICHT enthalten */
const NUR_SUPER_ADMIN: readonly FunktionsId[] = ['kZusammen', 'vEmail', 'vBranding', 'vModule', 'vAudit', 'vLes', 'vSysBackup', 'vRollen'];
const ADMIN: readonly FunktionsId[] = ALLE_IDS.filter(id => !NUR_SUPER_ADMIN.includes(id));

/** Rechte der „Betrachter“-Rolle gelten nie für diese Funktionen — auf keinem Weg (auch nicht als Ersteller oder DS-Admin) */
const BETRACHTER_GRENZE: readonly FunktionsId[] = ['pHistorie', 'hSensibel', 'hBank', 'hNotiz'];

export type VorlagenName =
  | 'SUPER_ADMIN' | 'ADMIN' | 'EREIGNISVERANTWORTLICHER' | 'MITBEARBEITER' | 'BETRACHTER'
  | 'VERANTWORTLICH' | 'BEARBEITEN' | 'BETRACHTEN'
  | 'DS_ADMIN' | 'DS_VIEWER' | 'ERSTELLER';

export const STANDARD_VORLAGEN: {
  SUPER_ADMIN: RollenVorlage; ADMIN: RollenVorlage; EREIGNISVERANTWORTLICHER: RollenVorlage; MITBEARBEITER: RollenVorlage; BETRACHTER: RollenVorlage;
  VERANTWORTLICH: WegVorlage; BEARBEITEN: WegVorlage; BETRACHTEN: WegVorlage; DS_ADMIN: WegVorlage; DS_VIEWER: WegVorlage; ERSTELLER: WegVorlage;
} = {
  SUPER_ADMIN: { ueberall: ALLE_IDS, zugewiesen: ALLE_IDS, grenze: [] },
  ADMIN: { ueberall: ADMIN, zugewiesen: ADMIN, grenze: [] },
  EREIGNISVERANTWORTLICHER: { ueberall: EREIGNISVERANTWORTLICHER, zugewiesen: EREIGNISVERANTWORTLICHER, grenze: [] },
  // Der Planungsrechner steht allen Rollen der Verwaltungs-App offen
  MITBEARBEITER: { ueberall: ['bPlanrechner'], zugewiesen: VERANTWORTLICH_OHNE_KARTEI, grenze: [] },
  BETRACHTER: { ueberall: ['bPlanrechner'], zugewiesen: VERANTWORTLICH_OHNE_KARTEI.filter(id => id !== 'pLeeren'), grenze: BETRACHTER_GRENZE },
  VERANTWORTLICH: { erlaubt: VERANTWORTLICH },
  BEARBEITEN: { erlaubt: BEARBEITEN },
  BETRACHTEN: { erlaubt: ANSICHT },
  DS_ADMIN: { erlaubt: DS_ADMIN },
  DS_VIEWER: { erlaubt: ANSICHT },
  ERSTELLER: { erlaubt: ERSTELLER },
};

// ---------- Kontext und Ereignis ----------

/** Abweichung von der Vorlage: `null`/fehlend = wie die Vorlage */
export interface RechtAbweichung {
  ueberall?: boolean | null;
  zugewiesen?: boolean | null;
  /** Ereignisse (Kennungen), in denen das Konto die Funktion zusätzlich hat — es muss dort Mitbearbeiter sein */
  einzeln?: number[];
  /** Ausdrückliches Verbot: gilt auf allen Wegen, nur der Super-Admin ist ausgenommen */
  verbot?: boolean;
}
export type RechtAbweichungen = Partial<Record<FunktionsId, RechtAbweichung>>;
/** Abweichung einer Rolle: nur „überall“ und „zugewiesen“ — ein Verbot und einzelne Ereignisse gibt es nur am Konto */
export type RollenAbweichung = Omit<RechtAbweichung, 'verbot' | 'einzeln'>;
export type RollenAbweichungen = Partial<Record<FunktionsId, RollenAbweichung>>;

/** Rollen eines Weges (Mitbearbeiter-Rollen im Ereignis, DS-Admin, DS-Viewer) — bearbeitbar als Vorlagen (Etappe 3) */
export type WegName = 'VERANTWORTLICH' | 'BEARBEITEN' | 'BETRACHTEN' | 'DS_ADMIN' | 'DS_VIEWER';
/** Abweichung einer Wegvorlage vom Code-Standard: `true`/`false` je Funktion (fehlt = Standard) */
export type WegAbweichungen = Partial<Record<WegName, Partial<Record<FunktionsId, boolean>>>>;

/** Alles, was die Prüfung über eine Person wissen muss — ein reines Datenobjekt (kommt im Backend aus `req.user.rechte`) */
export interface RechteKontext {
  userId: number;
  /** Grundrolle als Zeichenkette (unbekannte Rollen gelten wie MITBEARBEITER, wie bisher) */
  grundrolle: string;
  /** Abweichungen der Rolle von den Standardwerten — Etappe 1: immer leer */
  rollenAbweichungen: RollenAbweichungen;
  /** Abweichungen am Konto — Etappe 1: immer leer */
  kontoAbweichungen: RechtAbweichungen;
  /** Dienststellen als DS-Admin / DS-Viewer */
  dsAdminIds: number[];
  dsViewerIds: number[];
  /** Abweichungen der Wegvorlagen (Mitbearbeiter-Rollen, DS-Admin, DS-Viewer) — gelten für alle Konten, nur mit Modul */
  wegAbweichungen?: WegAbweichungen;
  /** Zusatzmodul „Rollen & Rechte“ gebucht? Ohne Modul gelten nur die Standardvorlagen (Etappe 1: immer false) */
  modulAktiv: boolean;
  /** Eigene Organisation (nur für die feste Regel „Bearbeiten“ bei Helfenden) */
  organisation?: string | null;
}

export interface RechteEreignis {
  id?: number;
  createdById: number;
  dienststelleId?: number | null;
  /** Fehlt die Liste, gilt am Ereignis nur das Recht „überall“ (fail-closed) */
  collaborators?: { userId: number; role?: string }[];
}

export interface RechteZusatz {
  /** Organisation des Helfers (nur für `hBearbeiten`: Mitbearbeiter „Bearbeiten“ darf nur eigene Organisation) */
  helferOrganisation?: string | null;
  /**
   * Nur für Tore vor einem Handler, der die Organisation des Helfers selbst prüft (`hBearbeiten`): Die Organisationsregel zählt hier
   * nicht — gefragt ist, ob die Funktion auf irgendeinem Weg offen ist. Nie für die Entscheidung über einen einzelnen Helfer verwenden.
   */
  organisationPruefenSpaeter?: boolean;
}

export const neuerKontext = (
  userId: number,
  grundrolle: string,
  dsAdminIds: number[] = [],
  dsViewerIds: number[] = [],
  organisation: string | null = null,
): RechteKontext => ({
  userId, grundrolle, rollenAbweichungen: {}, kontoAbweichungen: {}, dsAdminIds, dsViewerIds, modulAktiv: false, organisation,
});

// ---------- Prüfung ----------

const grundVorlage = (rolle: string): RollenVorlage => {
  switch (rolle) {
    case 'SUPER_ADMIN': return STANDARD_VORLAGEN.SUPER_ADMIN;
    case 'ADMIN': return STANDARD_VORLAGEN.ADMIN;
    case 'EREIGNISVERANTWORTLICHER': return STANDARD_VORLAGEN.EREIGNISVERANTWORTLICHER;
    case 'BETRACHTER': return STANDARD_VORLAGEN.BETRACHTER;
    default: return STANDARD_VORLAGEN.MITBEARBEITER;
  }
};

/** Mitbearbeiter-Rolle; eine unbekannte oder fehlende Rolle verhält sich wie „Bearbeiten“ (so rechnete der alte Code) */
const collabVorlage = (rolle: string | undefined): { name: 'VERANTWORTLICH' | 'BEARBEITEN' | 'BETRACHTEN'; erlaubt: readonly FunktionsId[] } =>
  rolle === 'VERANTWORTLICH' ? { name: 'VERANTWORTLICH', erlaubt: STANDARD_VORLAGEN.VERANTWORTLICH.erlaubt }
    : rolle === 'BETRACHTEN' ? { name: 'BETRACHTEN', erlaubt: STANDARD_VORLAGEN.BETRACHTEN.erlaubt }
      : { name: 'BEARBEITEN', erlaubt: STANDARD_VORLAGEN.BEARBEITEN.erlaubt };

/** Wirksame Abweichung: Konto vor Rolle; nur mit gebuchtem Modul */
const abweichung = (k: RechteKontext, f: FunktionsId): { ueberall: boolean | null; zugewiesen: boolean | null; einzeln: number[]; verbot: boolean } => {
  // Etappe 9 (V2): feste Regeln kennen keine Abweichung (gespeicherte ruhen)
  if (!k.modulAktiv || ohneAbweichung(f)) return { ueberall: null, zugewiesen: null, einzeln: [], verbot: false };
  const r = k.rollenAbweichungen[f] || {};
  const c = k.kontoAbweichungen[f] || {};
  const nimm = (a: boolean | null | undefined, b: boolean | null | undefined): boolean | null => (a !== undefined && a !== null ? a : b !== undefined && b !== null ? b : null);
  return { ueberall: nimm(c.ueberall, r.ueberall), zugewiesen: nimm(c.zugewiesen, r.zugewiesen), einzeln: c.einzeln || [], verbot: !!c.verbot };
};

/** Erlaubt die Wegvorlage die Funktion? Mit gebuchtem Modul zählt eine Abweichung der Vorlage, sonst der Code-Standard */
const wegDarf = (k: RechteKontext, name: WegName, f: FunktionsId): boolean => {
  const a = k.modulAktiv && !ohneAbweichung(f) ? k.wegAbweichungen?.[name]?.[f] : undefined;
  return a !== undefined ? a : STANDARD_VORLAGEN[name].erlaubt.includes(f);
};

/** Weg „einzeln“: bei einer Funktion aus `SCHREIB_FUNKTIONEN` wirkt er nicht für einen Mitbearbeiter mit der Rolle „Betrachten“ */
const einzelnWirkt = (rolle: string | undefined, f: FunktionsId): boolean => !(rolle === 'BETRACHTEN' && brauchtSchreibrecht(f));

const gleicheOrganisation = (a: string | null | undefined, b: string | null | undefined): boolean =>
  !!a && !!b && a.toLowerCase() === b.toLowerCase();

/**
 * Darf diese Person die Funktion — im Ereignis `ereignis` (Funktionen der Art „e“) bzw. im ganzen System (ohne Ereignis)?
 * Ohne Ereignis zählt bei Ereignis-Funktionen nur „überall“; System-Funktionen gelten zusätzlich für DS-Admins mit mindestens
 * einer Dienststelle.
 */
export const darf = (k: RechteKontext, f: FunktionsId, ereignis?: RechteEreignis | null, zusatz?: RechteZusatz): boolean => {
  if (k.grundrolle === 'SUPER_ADMIN') return true;
  // #673: Die Grundrolle HELFER (und jede unbekannte) hat keine Funktion — auch nicht als Mitbearbeiter, einzeln, über DS-Zuweisung oder als Ersteller (Positivliste, fail-closed)
  if (keineVerwaltungsRolle(k)) return false;
  if (verwaltungAusgeschlossen(k, f)) return false;
  // Etappe 6: Ansehen ist Voraussetzung jeder Ereignis-Funktion — wer ein Ereignis nicht sehen darf, kann darin nichts tun (ohne Ereignis:
  // nur mit „Ansehen überall“). In den Standardvorlagen ohnehin immer erfüllt (jeder Weg, der eine Ereignis-Funktion gibt, enthält das Ansehen).
  if (DEF[f].art === 'e' && f !== 'eAnsehen' && !darf(k, 'eAnsehen', ereignis)) return false;
  const ab = abweichung(k, f);
  if (ab.verbot) return false;
  const vorlage = grundVorlage(k.grundrolle);
  // Grenzfunktion (Betrachter-Grenze) ohne wirksame Spalte „überall“: Nur zwei Wege öffnen sie, beide setzen voraus, dass das Konto im
  // Ereignis Mitbearbeiter ist — (a) „zugewiesen“ ausdrücklich an UND die Wegvorlage der Mitbearbeiter-Rolle erlaubt es, (b) das
  // Ereignis ist am Konto einzeln ausgewählt. DS-Admin, DS-Viewer und Ersteller öffnen sie nie (sie kennen die Wegvorlage der
  // Mitbearbeiter-Rolle nicht); ohne Ereignis zählt nichts.
  if (vorlage.grenze.includes(f) && ab.ueberall !== true) {
    const collabG = ereignis && Array.isArray(ereignis.collaborators) ? ereignis.collaborators.find(c => c.userId === k.userId) : undefined;
    if (!collabG) return false;
    if (ereignis!.id !== undefined && ab.einzeln.includes(ereignis!.id) && einzelnWirkt(collabG.role, f)) return true;
    return ab.zugewiesen === true && wegDarf(k, collabVorlage(collabG.role).name, f);
  }

  const ueberall = ab.ueberall !== null ? ab.ueberall : vorlage.ueberall.includes(f);
  if (ueberall) return true;

  // Etappe 10: Verwaltungsfunktionen nie über den Weg DS-Admin (auch nicht über eine Wegvorlage)
  if (DEF[f].art === 's') return DEF[f].gruppe !== 'Verwaltung' && k.dsAdminIds.length > 0 && wegDarf(k, 'DS_ADMIN', f);
  if (!ereignis) return false;

  // zugewiesen: Mitbearbeiter von E, Spalte an UND Mitbearbeiter-Rolle erlaubt es (Obergrenze)
  const collab = Array.isArray(ereignis.collaborators) ? ereignis.collaborators.find(c => c.userId === k.userId) : undefined;
  if (collab) {
    const spalte = ab.zugewiesen !== null ? ab.zugewiesen : vorlage.zugewiesen.includes(f);
    const rolle = collabVorlage(collab.role);
    if (spalte && wegDarf(k, rolle.name, f)) {
      // Feste Zusatzregel: Mitbearbeiter „Bearbeiten“ ändert nur Helfende der eigenen Organisation
      if (f !== 'hBearbeiten' || rolle.name !== 'BEARBEITEN') return true;
      if (zusatz && (zusatz.organisationPruefenSpaeter || gleicheOrganisation(k.organisation, zusatz.helferOrganisation))) return true;
    }
    // einzeln: das Ereignis ist am Konto ausgewählt und das Konto ist dort Mitbearbeiter
    if (ereignis.id !== undefined && ab.einzeln.includes(ereignis.id) && einzelnWirkt(collab.role, f)) return true;
  }

  const ds = ereignis.dienststelleId;
  if (ds) {
    if (k.dsAdminIds.includes(ds) && wegDarf(k, 'DS_ADMIN', f)) return true;
    if (k.dsViewerIds.includes(ds) && wegDarf(k, 'DS_VIEWER', f)) return true;
  }
  return ereignis.createdById === k.userId && STANDARD_VORLAGEN.ERSTELLER.erlaubt.includes(f);
};

// ---------- Ereignisse (Etappe 6): Ansehen als Tor, Anlegen, Lese-Gast ----------

/**
 * Gilt die Spalte „überall“ der Funktion für dieses Konto wirksam (Rolle, Abweichung der Rolle und des Kontos, Verbot — bei Ereignis-
 * Funktionen zusätzlich „Ansehen überall“)? Ohne Modul: die Spalte der Standardvorlage der Grundrolle.
 * Für `eAnsehen` ist das „sieht alle Ereignisse“, für `eAnlegen`/`eBearbeiten` „darf die Dienststelle frei wählen“.
 */
export const ueberallWirksam = (k: RechteKontext, f: FunktionsId): boolean => {
  if (k.grundrolle === 'SUPER_ADMIN') return true;
  if (keineVerwaltungsRolle(k)) return false;
  if (verwaltungAusgeschlossen(k, f)) return false;
  const ab = abweichung(k, f);
  if (ab.verbot) return false;
  const vorlage = grundVorlage(k.grundrolle);
  const spalte = vorlage.grenze.includes(f) ? ab.ueberall === true : (ab.ueberall !== null ? ab.ueberall : vorlage.ueberall.includes(f));
  if (!spalte) return false;
  return DEF[f].art === 'e' && f !== 'eAnsehen' ? ueberallWirksam(k, 'eAnsehen') : true;
};

/**
 * Abstrakte Beschreibung der Ereignisse mit dem Recht „Ereignisse ansehen“ — dieselbe Regel wie `darf(k, 'eAnsehen', E)`, aber als
 * Daten, aus denen das Backend eine Datenbank-Bedingung baut (`permissions.ts`, `sichtbarkeitWhere`). Ohne Prisma-Vokabular, damit
 * die Frontend-Kopie identisch bleibt. Ein Test vergleicht beides über das ganze Raster, auch mit Abweichungen.
 *
 * Eine unbekannte oder fehlende Mitbearbeiter-Rolle wirkt wie „Bearbeiten“ (`collabVorlage`): `bearbeiten` steht daher für „jede Rolle außer
 * VERANTWORTLICH und BETRACHTEN“.
 */
export interface AnsehenBedingung {
  /** Alle Ereignisse (Spalte „überall“) */
  alle: boolean;
  /** Selbst angelegte Ereignisse */
  ersteller: boolean;
  /** Ereignisse, in denen das Konto Mitbearbeiter mit einer dieser Rollen ist */
  mitbearbeiter: { verantwortlich: boolean; bearbeiten: boolean; betrachten: boolean };
  /** Einzeln ausgewählte Ereignisse (das Konto muss dort Mitbearbeiter sein) */
  einzeln: number[];
  /** Ereignisse dieser Dienststellen (DS-Admin und DS-Viewer, soweit die Wegvorlage das Ansehen erlaubt) */
  dienststellen: number[];
}

export const ansehenBedingung = (k: RechteKontext): AnsehenBedingung => {
  const keine: AnsehenBedingung = { alle: false, ersteller: false, mitbearbeiter: { verantwortlich: false, bearbeiten: false, betrachten: false }, einzeln: [], dienststellen: [] };
  if (k.grundrolle === 'SUPER_ADMIN') return { ...keine, alle: true };
  if (keineVerwaltungsRolle(k)) return keine;
  const ab = abweichung(k, 'eAnsehen');
  if (ab.verbot) return keine;
  const vorlage = grundVorlage(k.grundrolle);
  if (ab.ueberall !== null ? ab.ueberall : vorlage.ueberall.includes('eAnsehen')) return { ...keine, alle: true };
  const spalte = ab.zugewiesen !== null ? ab.zugewiesen : vorlage.zugewiesen.includes('eAnsehen');
  const ds = new Set<number>();
  for (const id of k.dsAdminIds) if (wegDarf(k, 'DS_ADMIN', 'eAnsehen')) ds.add(id);
  for (const id of k.dsViewerIds) if (wegDarf(k, 'DS_VIEWER', 'eAnsehen')) ds.add(id);
  return {
    alle: false,
    ersteller: true,
    mitbearbeiter: {
      verantwortlich: spalte && wegDarf(k, 'VERANTWORTLICH', 'eAnsehen'),
      bearbeiten: spalte && wegDarf(k, 'BEARBEITEN', 'eAnsehen'),
      betrachten: spalte && wegDarf(k, 'BETRACHTEN', 'eAnsehen'),
    },
    einzeln: [...ab.einzeln],
    dienststellen: [...ds],
  };
};

/** Zu `ansehenBedingung` passend im Speicher geprüft (die Datenbank-Fassung baut `sichtbarkeitWhere`); dient dem Test „Regel = Bedingung“ */
export const passtAnsehenBedingung = (b: AnsehenBedingung, userId: number, e: RechteEreignis): boolean => {
  if (b.alle) return true;
  if (b.ersteller && e.createdById === userId) return true;
  const c = Array.isArray(e.collaborators) ? e.collaborators.find(x => x.userId === userId) : undefined;
  if (c) {
    const typ = c.role === 'VERANTWORTLICH' ? 'verantwortlich' : c.role === 'BETRACHTEN' ? 'betrachten' : 'bearbeiten';
    if (b.mitbearbeiter[typ]) return true;
    if (e.id !== undefined && b.einzeln.includes(e.id)) return true;
  }
  return !!e.dienststelleId && b.dienststellen.includes(e.dienststelleId);
};

/**
 * Für welche Dienststellen darf das Konto die Funktion ausüben, ohne dass ein bestimmtes Ereignis feststeht (Anlegen, Listen, Tore)?
 *  - `'alle'`: wer die Funktion „überall“ hat (Administration, Ereignisverantwortliche, oder per Abweichung);
 *  - sonst die Dienststellen, für die das Konto DS-Admin ist und die Wegvorlage DS-Admin die Funktion (bei Ereignis-Funktionen auch das
 *    Ansehen) erlaubt (Pflichtangabe). Ein Verbot am Konto gilt auf allen Wegen.
 * Etappe 7: verallgemeinert aus `anlegenDienststellen` (das bleibt als Hülle für „anlegen“ und „bearbeiten“).
 */
export const dienststellenFuer = (k: RechteKontext, funktion: FunktionsId): 'alle' | number[] => {
  if (ueberallWirksam(k, funktion)) return 'alle';
  if (keineVerwaltungsRolle(k)) return [];
  if (k.grundrolle !== 'SUPER_ADMIN' && abweichung(k, funktion).verbot) return [];
  if (DEF[funktion].gruppe === 'Verwaltung') return [];
  if (DEF[funktion].art === 'e' && funktion !== 'eAnsehen' && !wegDarf(k, 'DS_ADMIN', 'eAnsehen')) return [];
  return wegDarf(k, 'DS_ADMIN', funktion) ? [...k.dsAdminIds] : [];
};

export const anlegenDienststellen = (k: RechteKontext, funktion: 'eAnlegen' | 'eBearbeiten' | 'bKalkulation' = 'eAnlegen'): 'alle' | number[] =>
  dienststellenFuer(k, funktion);

/**
 * Etappe 8: Steht am Konto ein ausdrückliches Verbot der Funktion? Nur das Verbot am Konto (nicht „überall aus“, nicht die Abweichung
 * einer Rolle). Der Super-Admin hat nie ein Verbot, ohne gebuchtes Modul gibt es keines. Für Wege, die eine feste Regel ODER eine
 * Funktion kennen (öffentliches PDF, öffentlicher Link: Ersteller/Mitbearbeiter): Die feste Regel gilt nur, solange kein Verbot steht —
 * sonst wäre „ein Verbot gilt auf allen Wegen“ gebrochen. `funktionAbgeschaltet` taugt dafür nicht: dort sperrt auch „überall aus“.
 */
export const hatVerbot = (k: RechteKontext, f: FunktionsId): boolean => {
  if (k.grundrolle === 'SUPER_ADMIN') return false;
  return abweichung(k, f).verbot;
};

/**
 * Kann das Konto die Funktion irgendwo ausüben — ein grobes Tor für Seiten und Listen (die Entscheidung je Ereignis trifft `darf`)?
 * Immer: Rechte „überall“ und der Weg DS-Admin / DS-Viewer. Mit gebuchtem Modul zusätzlich, wenn eine ausdrückliche Abweichung
 * (Spalte „in zugewiesenen Ereignissen“, einzelne Ereignisse, eine Wegvorlage) die Funktion für Mitbearbeitende öffnet, die sie
 * nach dem Standard nicht hätten. Ohne Abweichung also dasselbe Ergebnis mit und ohne Modul.
 * Grobes Tor (Seite/Liste sichtbar), nie allein als Datenfreigabe lesen: jede Antwort danach je Ereignis über darf()/darfAm() filtern.
 */
export const kannIrgendwo = (k: RechteKontext, funktion: FunktionsId): boolean => {
  if (k.grundrolle === 'SUPER_ADMIN') return true;
  if (keineVerwaltungsRolle(k)) return false;
  const ds = dienststellenFuer(k, funktion);
  if (ds === 'alle' || ds.length > 0) return true;
  const ab = abweichung(k, funktion);
  if (ab.verbot) return false;
  if (DEF[funktion].gruppe !== 'Verwaltung' && k.dsViewerIds.length > 0 && wegDarf(k, 'DS_VIEWER', funktion) && (DEF[funktion].art !== 'e' || wegDarf(k, 'DS_VIEWER', 'eAnsehen'))) return true;
  // Spalte „überall“ gilt, das Ansehen aber nur eingeschränkt (z. B. Mitbearbeiter in einem Ereignis): `darf` erfüllt die Funktion dort
  // trotzdem (die Spalte zählt, sobald das Ereignis sichtbar ist) — das Tor muss es auch tun. Der Rest der Entscheidung bleibt je Ereignis.
  if (DEF[funktion].art === 'e' && funktion !== 'eAnsehen' && DEF[funktion].gruppe !== 'Verwaltung') {
    const vl = grundVorlage(k.grundrolle);
    const spalteUeberall = vl.grenze.includes(funktion) ? ab.ueberall === true : (ab.ueberall !== null ? ab.ueberall : vl.ueberall.includes(funktion));
    if (spalteUeberall) {
      const sicht = ansehenBedingung(k);
      if (sicht.alle || sicht.einzeln.length > 0 || sicht.dienststellen.length > 0 || Object.values(sicht.mitbearbeiter).some(Boolean)) return true;
    }
  }
  if (!k.modulAktiv || DEF[funktion].art === 's') return false;
  // Als Mitbearbeitender braucht das Konto das Ansehen: Verbot oder abgeschaltete Spalte „zugewiesen“ schließt den Weg
  if (funktion !== 'eAnsehen') {
    const an = abweichung(k, 'eAnsehen');
    if (an.verbot || an.zugewiesen === false) return false;
  }
  if (ab.einzeln.length > 0) return true;
  const vorlage = grundVorlage(k.grundrolle);
  const spalteStandard = vorlage.zugewiesen.includes(funktion);
  const spalte = ab.zugewiesen !== null ? ab.zugewiesen : spalteStandard;
  if (!spalte) return false;
  if (ab.zugewiesen === true && !spalteStandard) return (['VERANTWORTLICH', 'BEARBEITEN', 'BETRACHTEN'] as const).some(n => wegDarf(k, n, funktion));
  // Spalte schon im Standard: nur eine Wegvorlage, die die Funktion gegenüber dem Standard öffnet, zählt als ausdrückliche Abweichung
  return (['VERANTWORTLICH', 'BEARBEITEN', 'BETRACHTEN'] as const).some(n => wegDarf(k, n, funktion) && !STANDARD_VORLAGEN[n].erlaubt.includes(funktion));
};

/**
 * Sieht das Konto das Ereignis NUR über eine Abweichung („Ansehen überall“ für ein Konto, dessen Grundrolle es nicht hat) — ein
 * „Lese-Gast“. Er wird wie ein DS-Viewer behandelt (Feldmaske fail-closed, keine Dienstmappe, keine Kontaktsuche). Ohne Modul nie wahr.
 */
export const istLeseGast = (k: RechteKontext, ereignis: RechteEreignis): boolean =>
  k.modulAktiv && darf(k, 'eAnsehen', ereignis) && !darf({ ...k, modulAktiv: false }, 'eAnsehen', ereignis);

// ---------- Helfer-Felder und Kartei ----------

export interface HelferFeldMaske {
  /** Telefon und (im Detail) E-Mail */
  kontakt: boolean;
  /** E-Mail auch in Listen und Live-Meldungen */
  kontaktListe: boolean;
  /** Geburtsdatum, Adresse, Allergien, eigene Formularfelder (Art. 9) */
  sensibel: boolean;
  /** Kontoinhaber, IBAN, BIC */
  bank: boolean;
  /** Notizen */
  notiz: boolean;
}

/**
 * Welche Helfer-Felder diese Person im Ereignis sieht — löst `helferFilterKlasse`/`shouldFilter` ab. Fail-closed (#647):
 * Fehlt am Ereignis die Mitbearbeiter-Liste, gilt nur das Recht „überall“ (alle anderen sehen wie Betrachtende).
 */
export const helferFeldMaske = (k: RechteKontext, ereignis: RechteEreignis): HelferFeldMaske => {
  const listeFehlt = !Array.isArray(ereignis.collaborators);
  const ziel = listeFehlt ? undefined : ereignis;
  const sensibel = darf(k, 'hSensibel', ziel);
  const bank = darf(k, 'hBank', ziel);
  const notiz = darf(k, 'hNotiz', ziel);
  // Telefon sehen auch Betrachtende; wer das Ereignis ohne Liste erreicht, hat Zugriff (die Route hat ihn geprüft)
  const kontakt = listeFehlt ? true : darf(k, 'hKontakt', ereignis);
  // E-Mail in Listen bleibt für Betrachtende fest verborgen: nur, wer auch alles Übrige sieht
  return { kontakt, kontaktListe: kontakt && sensibel && bank && notiz, sensibel, bank, notiz };
};

/**
 * Kartei-Zugriff (Funktion „Kartei ansehen und übernehmen“) im Ereignis:
 *  - `'alles'`: die feste Rolle erlaubt es ohnehin (Administration, Ereignisverantwortliche) — die ganze Kartei, wie bisher;
 *  - `'gliederung'`: jedes andere Konto (auch mit „überall“ per Abweichung) sowie Administration/Ereignisverantwortliche mit
 *    eingeschränktem „überall“ — der Bereich ist auf die am Konto gepflegten Gliederungen begrenzt; ohne Gliederung gibt es keine Treffer (Pflicht, nie „alles“ durch Weglassen);
 *  - `null`: kein Recht.
 * Die Gliederungen selbst kennt diese reine Funktion nicht, sie löst `karteiBereich.service.ts` mit der Datenbank auf.
 */
export type KarteiBereichArt = 'alles' | 'gliederung' | null;

export const karteiBereich = (k: RechteKontext, ereignis?: RechteEreignis | null): KarteiBereichArt => {
  if (!darf(k, 'kUebernehmen', ereignis)) return null;
  if (k.grundrolle === 'SUPER_ADMIN') return 'alles';
  // Gliederung ist Pflicht: „Alles“ gibt es nur für Administration und Ereignisverantwortliche, solange die wirksame Spalte
  // „überall“ (Rolle und Abweichung am Konto) das Recht gibt. Jedes andere Konto — auch mit „überall“ per Abweichung — hat den
  // Gliederungsbereich („überall“ heißt dort nur: in allen Ereignissen); die ganze Kartei nur über die ausdrückliche Auswahl.
  const ab = abweichung(k, 'kUebernehmen');
  const ueberall = ab.ueberall !== null ? ab.ueberall : grundVorlage(k.grundrolle).ueberall.includes('kUebernehmen');
  const festeVolleRolle = k.grundrolle === 'ADMIN' || k.grundrolle === 'EREIGNISVERANTWORTLICHER';
  return ueberall && festeVolleRolle ? 'alles' : 'gliederung';
};

/**
 * Hat das Konto die Funktion ausdrücklich abgeschaltet (Verbot oder Spalte „überall“ auf nein, durch Konto oder Rolle)? Nur mit
 * gebuchtem Modul. Für Wege, die bisher an einer festen Rolle hängen und bei Abschaltung gesperrt werden (Kartei-Nachschlagen).
 */
export const funktionAbgeschaltet = (k: RechteKontext, f: FunktionsId): boolean => {
  if (k.grundrolle === 'SUPER_ADMIN') return false;
  const ab = abweichung(k, f);
  return ab.verbot || ab.ueberall === false;
};

// ---------- Kartei (Etappe 9) ----------

/**
 * Spalte „überall“ einer Funktion OHNE die Kette über „Ereignisse ansehen: überall“ (Etappe 9, E9 F4): Die Kartei hat kein Ereignis, ein
 * Konto mit Gliederung als Geltungsbereich soll Telefon oder Geburtsdatum der Karteieinträge seines Bereichs sehen, ohne dass man ihm alle
 * Ereignisse öffnet. Verbot zuerst, Betrachter-Grenze nur mit ausdrücklichem „überall“, Super-Admin immer; die Grundrolle HELFER nie.
 */
const ueberallOhneKette = (k: RechteKontext, f: FunktionsId): boolean => {
  if (k.grundrolle === 'HELFER') return false;
  if (k.grundrolle === 'SUPER_ADMIN') return true;
  const ab = abweichung(k, f);
  if (ab.verbot) return false;
  const vorlage = grundVorlage(k.grundrolle);
  if (vorlage.grenze.includes(f)) return ab.ueberall === true;
  return ab.ueberall !== null ? ab.ueberall : vorlage.ueberall.includes(f);
};

/**
 * Bereich der Funktion „Karteieinträge bearbeiten“ (Etappe 9): `'alles'` nur für den Super-Admin und die Grundrolle Administration mit
 * wirksamem „überall“; jedes andere Konto (auch Ereignisverantwortliche, auch über die Wegvorlage DS-Admin) hat den Gliederungsbereich
 * seiner Gliederungen (`KontoRechtGliederung.funktion = 'kBearbeiten'`), „die ganze Kartei“ nur über das ausdrückliche Häkchen.
 * Die Grundrolle HELFER nie (fail-closed, #673).
 */
export const karteiBearbeitenBereich = (k: RechteKontext): KarteiBereichArt => {
  if (k.grundrolle === 'HELFER') return null;
  if (!darf(k, 'kBearbeiten')) return null;
  if (k.grundrolle === 'SUPER_ADMIN') return 'alles';
  const ab = abweichung(k, 'kBearbeiten');
  const ueberall = ab.ueberall !== null ? ab.ueberall : grundVorlage(k.grundrolle).ueberall.includes('kBearbeiten');
  return ueberall && k.grundrolle === 'ADMIN' ? 'alles' : 'gliederung';
};

/**
 * Einzelansicht eines Karteieintrags und Lese-Wege: die ganze Kartei, wer „Kartei ansehen und übernehmen“ mit dem Bereich „alles“ hat
 * (Administration, Ereignisverantwortliche) oder „Karteieinträge bearbeiten“ für die ganze Kartei; sonst der Bereich von „bearbeiten“.
 * Ein Konto mit „Kartei ansehen“ nur über eine Gliederung bekommt keine Einzelansicht (wie bisher 403).
 */
export const karteiLesen = (k: RechteKontext): KarteiBereichArt => {
  if (k.grundrolle === 'HELFER') return null;
  const u = karteiBereich(k);
  const b = karteiBearbeitenBereich(k);
  if (u === 'alles' || b === 'alles') return 'alles';
  return b === 'gliederung' ? 'gliederung' : null;
};

/** Zusammenführen und Duplikate: nur zusammen mit der ganzen Kartei (E9 F8) — die Suche zeigt Daten über alle Einträge, das Zusammenführen zieht Einschränkungen um */
export const karteiZusammenfuehren = (k: RechteKontext): boolean => darf(k, 'kZusammen') && karteiBearbeitenBereich(k) === 'alles';

/** Nachweise prüfen: wirkt nur auf Einträge im Bereich von „Karteieinträge bearbeiten“ (E9 F6) */
export const karteiNachweise = (k: RechteKontext): KarteiBereichArt => (darf(k, 'kNachweise') ? karteiBearbeitenBereich(k) : null);

/** Sperren, Entsperren, Löschen, Dienststellen zuweisen: nur mit der ganzen Kartei (E9 F2) */
export const karteiVerwalten = (k: RechteKontext): boolean => karteiBearbeitenBereich(k) === 'alles';

/** Kartei-Maske: Welche Feldgruppen eines Karteieintrags sieht das Konto? (nur Spalte „überall“, siehe `ueberallOhneKette`) */
export interface KarteiMaske {
  /** Telefon und E-Mail */
  kontakt: boolean;
  /** Geburtsdatum, Adresse, Allergien (Art. 9) */
  sensibel: boolean;
  /** Kontoinhaber, IBAN, BIC — „Bankdaten sehen“ UND „Karteieinträge bearbeiten“ (Ereignisverantwortliche sehen sie in der Kartei nie, #587) */
  bank: boolean;
  /** Notizen zu Helfenden (Admin-Notiz) — „Notizen“ UND „Karteieinträge bearbeiten“ */
  notiz: boolean;
}

export const karteiMaske = (k: RechteKontext): KarteiMaske => {
  const bearbeiten = karteiBearbeitenBereich(k) !== null;
  return {
    kontakt: ueberallOhneKette(k, 'hKontakt'),
    sensibel: ueberallOhneKette(k, 'hSensibel'),
    bank: bearbeiten && ueberallOhneKette(k, 'hBank'),
    notiz: bearbeiten && ueberallOhneKette(k, 'hNotiz'),
  };
};
